// The pay rule behind a tutor's cycle hours, following the server's pay rule (047 D85; amends
// D47, which had moved the old dashboard rule unchanged) so hours × rate is what Owed received.
// A completed lesson always counts, free trials included (047 D88 — the owner pays them, and the
// server does too). Pure: no React and no Firestore, so Jest loads it.
import { toDate } from "./ticketPresentation";

// Does a cancelled lesson still pay the tutor? The stored `countForTeacher` decides when the
// cancelling code wrote one. Without it: never after a refund, a donation, a free session or the
// tutor's own cancellation; otherwise only when the lesson was cancelled less than 2 hours before
// its start (2–4 hours before is a donation, 4 or more a refund). No times, no pay.
export const cancelledPaysTutor = (booking = {}) => {
  if (booking.countForTeacher === true) return true;
  if (booking.countForTeacher === false) return false;
  if (
    booking.studentRefund === true ||
    booking.donation === true ||
    booking.isFreeSession === true ||
    booking.cancelledBy === booking.teacherId
  ) {
    return false;
  }
  const cancelledAt = toDate(booking.cancelledAt);
  const start = toDate(booking.startTimeUTC);
  if (!cancelledAt || !start) return false;
  return (start.getTime() - cancelledAt.getTime()) / 3600000 < 2;
};

// Minutes between a lesson's start and end, rounded; null when either is missing or the end is
// not after the start (functions/finishLesson.js spanMinutes).
export const spanMinutes = (booking = {}) => {
  const start = toDate(booking.startTimeUTC);
  const end = toDate(booking.endTimeUTC);
  if (!start || !end || end <= start) return null;
  return Math.round((end.getTime() - start.getTime()) / 60000);
};

// A paid lesson's hours, as the server pays them: half an hour at 25 minutes, one hour otherwise
// (finishLesson hoursFor, cancelBooking creditsFor).
export const paidHours = (minutes) => (minutes === 25 ? 0.5 : 1);

// The hours a lesson pays its tutor, 0 when it pays nothing: a completed lesson always (free
// trials included, 047 D88), a cancelled one when cancelledPaysTutor says so (053 D1).
export const bookingPaidHours = (booking = {}) => {
  const status = String(booking.status || "").toLowerCase();
  if (status === "finished") return paidHours(spanMinutes(booking) ?? booking.duration);
  if (status === "cancelled" && cancelledPaysTutor(booking)) return paidHours(booking.duration);
  return 0;
};

// The rate a tutor had at `when` (053 D14): the first salaryHistory entry whose `until` is after
// it, else today's salary. The tutor app's rateAt (src/utils/teacherDashboard.js, spec 051), so
// both price a lesson alike. `pay` is the tutor document, or a bare rate.
export function rateAt(pay, when) {
  if (pay === null || typeof pay !== "object") return Number(pay) || 0;
  const t = toDate(when)?.getTime();
  if (t != null) {
    const past = (pay.salaryHistory || [])
      .map((h) => ({ rate: Number(h?.rate) || 0, until: toDate(h?.until)?.getTime() }))
      .filter((h) => h.until != null)
      .sort((a, b) => a.until - b.until);
    const hit = past.find((h) => t < h.until);
    if (hit) return hit.rate;
  }
  return Number(pay.salary) || 0;
}

// What paid lessons ([{ hours, start }]) earned, each at the rate its tutor had when it started.
// Rounded at each lesson, as cycleTeaching (cyclePay.js) rounds its running sum, so the two pages
// agree to the piastre (053 D14).
export const lessonsEarned = (lessons, pay) =>
  (lessons || []).reduce((sum, lesson) => Math.round((sum + lesson.hours * rateAt(pay, lesson.start)) * 100) / 100, 0);

export const emptyCycleDetails = () => ({
  lessons25min: 0,
  lessons55min: 0,
  cancelledCount: 0,
  cancelledCountForTeacher: 0,
  cancelledLessonIds: [],
});

// The two pay periods as the hours are compared with them. A period's `end` is exclusive (it is
// the next period's start), so its last instant is one millisecond before it.
export const cycleBounds = ({ current, previous }) => ({
  currentStart: current.start,
  currentEnd: new Date(current.end.getTime() - 1),
  previousStart: previous.start,
  previousEnd: new Date(previous.end.getTime() - 1),
});

// Hours per tutor in the current and the previous pay period, and the previous period's details,
// from `bookings` ([{ id, ...data }]). A completed lesson always counts, free trials included
// (047 D88 — the owner pays them, and the server does too); a cancelled one counts when it paid
// the tutor. A lesson belongs to the period its end time falls in; one with no tutor or no end
// time is skipped. The details also list every cancelled lesson of the previous period, paid or
// not. `previousLessons` lists the previous period's paid lessons per tutor ([{ hours, start }]),
// so a page can price each one at its own rate (053 D14).
export const cycleHours = (bookings, bounds) => {
  const current = {};
  const previous = {};
  const previousCycleDetails = {};
  const previousLessons = {};
  const detailsFor = (teacherId) => {
    if (!previousCycleDetails[teacherId]) previousCycleDetails[teacherId] = emptyCycleDetails();
    return previousCycleDetails[teacherId];
  };
  (bookings || []).forEach((b) => {
    const status = String(b.status || "").toLowerCase();
    const isFinished = status === "finished";
    const isCancelled = status === "cancelled";
    const finishedPays = isFinished;
    const cancelledPays = isCancelled && cancelledPaysTutor(b);
    const end = toDate(b.endTimeUTC);
    if (!b.teacherId || !end) return;
    const inPrevious = end >= bounds.previousStart && end <= bounds.previousEnd;
    if (isCancelled && inPrevious) {
      const details = detailsFor(b.teacherId);
      details.cancelledCount += 1;
      details.cancelledLessonIds.push(b.id);
      if (cancelledPays) details.cancelledCountForTeacher += 1;
    }
    const hours = bookingPaidHours(b);
    if (!hours) return;
    const minutes = finishedPays ? spanMinutes(b) ?? b.duration : b.duration;
    if (end >= bounds.currentStart && end <= bounds.currentEnd) {
      current[b.teacherId] = (current[b.teacherId] || 0) + hours;
    }
    if (inPrevious) {
      previous[b.teacherId] = (previous[b.teacherId] || 0) + hours;
      (previousLessons[b.teacherId] = previousLessons[b.teacherId] || []).push({ hours, start: toDate(b.startTimeUTC) });
      const details = detailsFor(b.teacherId);
      if (finishedPays && minutes === 25) details.lessons25min += 1;
      else if (finishedPays && minutes === 55) details.lessons55min += 1;
    }
  });
  return { current, previous, previousCycleDetails, previousLessons };
};
